import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { useCallback, useState } from 'react';

import { ErrorResponse } from '../api/hscan';
import { Case, DicomDownloadEvent, HpacsLinkResponse } from '../api/hscan/case';
import {
  generateShareToPatientHpacsLinkId,
  requestShareToPatientCase,
  requestShareToPatientDicomImage,
  requestShareToPatientDownloadImage,
  requestShareToPatientSave,
  RequestShareToPatientSaveRequest,
} from '../api/hscan/shareToPatient';
import { HPACS_URL, HSCAN_BACKEND_API_URL } from '../env';

import {
  DownloadOptions,
  DownloadProgressEventHandler,
  useDownloadFile,
} from './caseHook';
import { MutationOption, QueryOption } from './react-query-type';

/**
 * 영상 목록
 */
export const useShareToPatientCase = (
  sessionId: string,
  options?: QueryOption<Case[]>,
) => {
  const fetch = useCallback(async () => {
    const { data } = await requestShareToPatientCase(sessionId);
    return data;
  }, [sessionId]);

  return useQuery({
    queryKey: ['requestShareToPatientCase', sessionId],
    queryFn: fetch,
    ...options,
  });
};

/**
 * 이미지
 */
export const useShareToPatientDicomImage = (
  imageId: string,
  sessionId: string,
) => {
  const fetch = useCallback(async () => {
    const { data } = await requestShareToPatientDicomImage(imageId, sessionId);
    return data;
  }, [imageId, sessionId]);
  return useQuery({
    queryKey: ['requestDicomImage', imageId],
    queryFn: fetch,
    staleTime: 1000 * 3600 * 24,
  });
};

/**
 * hpacs viewer link
 */
interface ShareToPatientLinkParams {
  caseId: string[];
  startDtime?: string;
  endDtime?: string;
  sessionId: string;
}
const useShareToPatientLink = (
  options?: MutationOption<
    ShareToPatientLinkParams,
    HpacsLinkResponse,
    AxiosError<ErrorResponse>
  >,
) => {
  const fetch = useCallback(async (params: ShareToPatientLinkParams) => {
    const { caseId, startDtime, endDtime, sessionId } = params;
    const now = startDtime ?? new Date().toISOString();
    const expire =
      endDtime ?? new Date(new Date().getTime() + 86400000).toISOString();
    const body = {
      caseId,
      startDtime: now,
      endDtime: expire,
    };
    const { data } = await generateShareToPatientHpacsLinkId(body, sessionId);
    return data;
  }, []);
  return useMutation({ mutationFn: fetch, ...options });
};

export const useGetShareToPatientLinkUrl = (sessionId: string) => {
  const { mutateAsync: requestLink } = useShareToPatientLink({
    onError(error) {
      console.warn(error.response?.status, error.response?.data);
    },
  });

  return useCallback(
    async (caseIds: string[]) => {
      const link = await requestLink({ caseId: caseIds, sessionId });
      return `${HPACS_URL}/linkLogin/${link?.id}`;
    },
    [requestLink, sessionId],
  );
};

/**
 * 다운로드
 */
export const useShareToPatientSubscribeDownloadProgress = (
  sseKey: string,
  downloadProgressEventHandler: DownloadProgressEventHandler,
  sessionId: string,
) => {
  const [progress, setProgress] = useState<number>(0);
  const [currentCase, setCurrentCase] = useState<number>(1);
  const [eventSource, setEventSource] = useState<EventSourcePolyfill | null>(
    null,
  );
  const subscribeDownloadProgress = () => {
    const eventSource = new EventSourcePolyfill(
      `${HSCAN_BACKEND_API_URL}/share-to-patient/case/dicom/sse/${sseKey}/event`,
      {
        heartbeatTimeout: 60 * 60 * 1000,
        withCredentials: true,
        headers: {
          SESSIONID: sessionId,
        },
      },
    );
    let progress = 0;
    let currentCase = 1;

    eventSource.onmessage = ev => {
      progress = progress + 1;
      const eventData = JSON.parse(ev.data) as DicomDownloadEvent;
      if (currentCase && currentCase !== eventData.completeCount) {
        currentCase = currentCase + 1;
        progress = 0;
      }
      if (eventData.eventId === 'DOWNLOAD') {
        downloadProgressEventHandler.onProgress({
          progress,
          totalImageCount: eventData.totalImageCount,
          currentCase,
          totalCaseCount: eventData.totalCount,
        });
      } else if (eventData.eventId === 'COMPRESS') {
        downloadProgressEventHandler.onCompress();
      }
      if (
        eventData.eventId === 'COMPLETE' ||
        (eventData.eventId === 'COMPRESS' && // COMPLETE가 오지 않을 때를 방지하기 위한 로직
          eventData.completeCount === eventData.totalCount &&
          progress === eventData.totalImageCount)
      ) {
        downloadProgressEventHandler.onDownload();
        eventSource.close();
      }
    };

    eventSource.onerror = _ => {
      eventSource.close();
      progress = 0;
      currentCase = 1;
    };
  };

  return { subscribeDownloadProgress };
};

export const useShareToPatientDownloadImage = (
  caseIds: string[],
  sseKey: string | undefined,
  sessionId: string,
  options?: DownloadOptions,
) => {
  // TODO: useDownloadFile 에서 header 정보를 사용하고 있어서 fetchData 타입을 AxiosResponse 로 유지함
  const fetchData = useCallback(async () => {
    if (sseKey) {
      return requestShareToPatientDownloadImage(sseKey, caseIds, sessionId);
    }
  }, [caseIds, sseKey, sessionId]);
  return useDownloadFile(fetchData, options);
};

export const useShareToPatientSave = (
  options?: MutationOption<RequestShareToPatientSaveRequest, void>,
) => {
  return useMutation({
    mutationFn: async params => {
      await requestShareToPatientSave(params);
    },
    ...options,
  });
};
